import { useState } from "react";
import { todayDubai } from "../../utils/dateUtils";

const DEFAULT_OBSERVATIONS = [
  { parameter: "PH",             standardValue: "", observedValue: "", okStatus: "" },
  { parameter: "CP (centipoise)",standardValue: "", observedValue: "", okStatus: "" },
  { parameter: "RPM",            standardValue: "", observedValue: "", okStatus: "" },
  { parameter: "Spindle",        standardValue: "", observedValue: "", okStatus: "" },
  { parameter: "Torque",         standardValue: "", observedValue: "", okStatus: "" },
  { parameter: "Temp °C",        standardValue: "", observedValue: "", okStatus: "" },
  { parameter: "Solid",          standardValue: "", observedValue: "", okStatus: "" },
  { parameter: "Dry Appearance", standardValue: "", observedValue: "", okStatus: "" },
];

function blankIngredient() {
  return { type: "OTHER", selectedId: "", name: "", code: "", qtyPct: "" };
}

function ingredientDisplayName(ing, rms, sfgs) {
  if (ing.type === "RM") {
    const rm = rms.find(r => String(r.id) === String(ing.selectedId));
    return rm ? rm.name : ing.selectedId;
  }
  if (ing.type === "SFG") {
    const sfg = sfgs.find(s => String(s.id) === String(ing.selectedId));
    return sfg ? sfg.name : ing.selectedId;
  }
  if (ing.type === "NEW_RM") {
    return ing.code.trim()
      ? `${ing.code.trim()} - ${ing.name.trim()}`
      : ing.name.trim();
  }
  return ing.name.trim();
}

function IngredientRow({ ing, idx, rms, sfgs, onChange, onRemove }) {
  function set(k, v) { onChange(idx, k, v); }

  return (
    <tr>
      <td style={td}>{idx + 1}</td>
      <td style={td} style={{ ...td, minWidth: 110 }}>
        <select className="form-select" style={{ margin: 0 }}
          value={ing.type}
          onChange={e => set("type", e.target.value)}>
          <option value="RM">RM</option>
          <option value="SFG">SFG</option>
          <option value="NEW_RM">New RM</option>
          <option value="OTHER">Other</option>
        </select>
      </td>
      <td style={td}>
        {ing.type === "RM" && (
          <select className="form-select" style={{ margin: 0 }}
            value={ing.selectedId}
            onChange={e => set("selectedId", e.target.value)}>
            <option value="">— Select RM —</option>
            {rms.map(rm => (
              <option key={rm.id} value={rm.id}>{rm.id} — {rm.name}</option>
            ))}
          </select>
        )}
        {ing.type === "SFG" && (
          <select className="form-select" style={{ margin: 0 }}
            value={ing.selectedId}
            onChange={e => set("selectedId", e.target.value)}>
            <option value="">— Select SFG —</option>
            {sfgs.map(sfg => (
              <option key={sfg.id} value={sfg.id}>{sfg.id} — {sfg.name}</option>
            ))}
          </select>
        )}
        {ing.type === "NEW_RM" && (
          <div style={{ display: "flex", gap: 4 }}>
            <input className="form-input" style={{ margin: 0, width: 90 }}
              placeholder="Code" value={ing.code}
              onChange={e => set("code", e.target.value)} />
            <input className="form-input" style={{ margin: 0, flex: 1 }}
              placeholder="Name *" value={ing.name}
              onChange={e => set("name", e.target.value)} />
          </div>
        )}
        {ing.type === "OTHER" && (
          <input className="form-input" style={{ margin: 0 }}
            placeholder="Ingredient name" value={ing.name}
            onChange={e => set("name", e.target.value)} />
        )}
      </td>
      <td style={td}>
        <input className="form-input" style={{ margin: 0 }}
          value={ing.qtyPct} placeholder="e.g. 45%"
          onChange={e => set("qtyPct", e.target.value)} />
      </td>
      <td style={td}>
        <button type="button" className="btn" style={{ padding: "2px 7px", fontSize: 13 }}
          onClick={() => onRemove(idx)}>✕</button>
      </td>
    </tr>
  );
}

export function NpdCreateModal({ trial, onSave, onCancel, rms = [], sfgs = [] }) {
  const isEdit = !!trial;

  const [form, setForm] = useState({
    prodCode:        trial?.prodCode        ?? "",
    date:            trial?.date            ?? todayDubai(),
    sampleBatchQty:  trial?.sampleBatchQty  ?? "",
    viscometerParams:trial?.viscometerParams ?? "",
    otherParams:     trial?.otherParams     ?? "",
    result:          trial?.result          ?? "IN_PROGRESS",
    remarks:         trial?.remarks         ?? "",
  });

  const [ingredients, setIngredients] = useState(() => {
    if (trial?.ingredients?.length) {
      return trial.ingredients.map(i => ({
        type: "OTHER", selectedId: "", name: i.name ?? "", code: "", qtyPct: i.qtyPct ?? "",
      }));
    }
    return [blankIngredient()];
  });

  const [observations, setObservations] = useState(() => {
    if (trial?.observations?.length) {
      return trial.observations.map(o => ({
        parameter:     o.parameter     ?? "",
        standardValue: o.standardValue ?? "",
        observedValue: o.observedValue ?? "",
        okStatus:      o.okStatus      ?? "",
      }));
    }
    return DEFAULT_OBSERVATIONS.map(o => ({ ...o }));
  });

  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState(null);

  function setF(k, v) { setForm(f => ({ ...f, [k]: v })); }

  function handleIngChange(idx, k, v) {
    setIngredients(list => list.map((r, i) => i === idx ? { ...r, [k]: v } : r));
  }
  function addIng()       { setIngredients(list => [...list, blankIngredient()]); }
  function removeIng(idx) { setIngredients(list => list.filter((_, i) => i !== idx)); }

  function setObs(idx, k, v) {
    setObservations(list => list.map((r, i) => i === idx ? { ...r, [k]: v } : r));
  }
  function addObs() {
    setObservations(list => [...list, { parameter: "", standardValue: "", observedValue: "", okStatus: "" }]);
  }
  function removeObs(idx) { setObservations(list => list.filter((_, i) => i !== idx)); }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const body = {
        ...form,
        date:             form.date || null,
        sampleBatchQty:   form.sampleBatchQty  || null,
        viscometerParams: form.viscometerParams || null,
        otherParams:      form.otherParams      || null,
        remarks:          form.remarks          || null,
        ingredients: ingredients
          .map(i => ({ ...i, _name: ingredientDisplayName(i, rms, sfgs) }))
          .filter(i => i._name)
          .map((i, idx) => ({ seqNo: idx + 1, name: i._name, qtyPct: i.qtyPct || null })),
        observations: observations
          .filter(o => o.parameter.trim())
          .map((o, idx) => ({
            seqNo:         idx + 1,
            parameter:     o.parameter.trim(),
            standardValue: o.standardValue || null,
            observedValue: o.observedValue || null,
            okStatus:      o.okStatus      || null,
          })),
      };
      await onSave(body);
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  }

  const sectionStyle = {
    fontWeight: 600, fontSize: 12, color: "#1a3a6b",
    borderBottom: "2px solid #1a3a6b", marginBottom: 12, paddingBottom: 4, marginTop: 16,
  };

  return (
    <div className="modal" style={{ width: 860 }}>
      <div className="modal-header">
        <div className="modal-title">{isEdit ? `Edit Trial — ${trial.trialNo}` : "New NPD Trial"}</div>
        <button className="modal-close" onClick={onCancel}>✕</button>
      </div>
      <form onSubmit={handleSubmit}>
        <div className="modal-body" style={{ maxHeight: "75vh", overflowY: "auto" }}>

          <div style={sectionStyle}>Trial Information</div>
          <div className="form-row">
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Product Code *</label>
              <input className="form-input" required value={form.prodCode}
                onChange={e => setF("prodCode", e.target.value)} />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Date</label>
              <input className="form-input" type="date" value={form.date}
                onChange={e => setF("date", e.target.value)} />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Sample Batch Qty</label>
              <input className="form-input" placeholder="e.g. 5 kg"
                value={form.sampleBatchQty}
                onChange={e => setF("sampleBatchQty", e.target.value)} />
            </div>
          </div>

          {/* ── Ingredients ── */}
          <div style={sectionStyle}>Ingredients</div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, marginBottom: 8 }}>
            <thead>
              <tr style={{ background: "#f1f5f9" }}>
                <th style={th}>#</th>
                <th style={th}>Type</th>
                <th style={th}>Ingredient</th>
                <th style={{ ...th, width: 110 }}>Qty / %</th>
                <th style={{ ...th, width: 40 }}></th>
              </tr>
            </thead>
            <tbody>
              {ingredients.map((ing, idx) => (
                <IngredientRow
                  key={idx}
                  ing={ing}
                  idx={idx}
                  rms={rms}
                  sfgs={sfgs}
                  onChange={handleIngChange}
                  onRemove={removeIng}
                />
              ))}
            </tbody>
          </table>
          <button type="button" className="btn" style={{ fontSize: 12, marginBottom: 4 }}
            onClick={addIng}>+ Add Ingredient</button>

          {/* ── Parameters ── */}
          <div style={sectionStyle}>Parameters</div>
          <div className="form-row">
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Viscometer Parameters</label>
              <textarea className="form-input" rows={3}
                placeholder="e.g. Brookfield RV, Spindle 3, 20 rpm"
                value={form.viscometerParams}
                onChange={e => setF("viscometerParams", e.target.value)} />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Other Parameters</label>
              <textarea className="form-input" rows={3}
                placeholder="Any other test conditions"
                value={form.otherParams}
                onChange={e => setF("otherParams", e.target.value)} />
            </div>
          </div>

          {/* ── Observations ── */}
          <div style={sectionStyle}>Observations</div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, marginBottom: 8 }}>
            <thead>
              <tr style={{ background: "#f1f5f9" }}>
                <th style={th}>#</th>
                <th style={th}>Parameter</th>
                <th style={th}>Standard Value</th>
                <th style={th}>Observed Value</th>
                <th style={th}>OK / Status</th>
                <th style={{ ...th, width: 40 }}></th>
              </tr>
            </thead>
            <tbody>
              {observations.map((obs, idx) => (
                <tr key={idx}>
                  <td style={td}>{idx + 1}</td>
                  <td style={td}>
                    <input className="form-input" style={{ margin: 0 }}
                      value={obs.parameter}
                      onChange={e => setObs(idx, "parameter", e.target.value)} />
                  </td>
                  <td style={td}>
                    <input className="form-input" style={{ margin: 0 }}
                      value={obs.standardValue} placeholder="—"
                      onChange={e => setObs(idx, "standardValue", e.target.value)} />
                  </td>
                  <td style={td}>
                    <input className="form-input" style={{ margin: 0 }}
                      value={obs.observedValue} placeholder="—"
                      onChange={e => setObs(idx, "observedValue", e.target.value)} />
                  </td>
                  <td style={td}>
                    <select className="form-select" style={{ margin: 0 }}
                      value={obs.okStatus}
                      onChange={e => setObs(idx, "okStatus", e.target.value)}>
                      <option value="">—</option>
                      <option value="OK">OK</option>
                      <option value="NOT OK">NOT OK</option>
                      <option value="N/A">N/A</option>
                    </select>
                  </td>
                  <td style={td}>
                    <button type="button" className="btn" style={{ padding: "2px 7px", fontSize: 13 }}
                      onClick={() => removeObs(idx)}>✕</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button type="button" className="btn" style={{ fontSize: 12, marginBottom: 4 }}
            onClick={addObs}>+ Add Row</button>

          {/* ── Result & Remarks ── */}
          <div style={sectionStyle}>Result</div>
          <div className="form-row">
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Result</label>
              <select className="form-select" value={form.result}
                onChange={e => setF("result", e.target.value)}>
                <option value="IN_PROGRESS">In Progress</option>
                <option value="PASS">Pass</option>
                <option value="FAIL">Fail</option>
              </select>
            </div>
            <div className="form-group" style={{ flex: 2 }}>
              <label className="form-label">Remarks</label>
              <input className="form-input" value={form.remarks}
                onChange={e => setF("remarks", e.target.value)} />
            </div>
          </div>

          {error && <div className="alert-warn" style={{ marginTop: 8 }}>{error}</div>}
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onCancel}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? "Saving…" : isEdit ? "Save Changes" : "Create Trial"}
          </button>
        </div>
      </form>
    </div>
  );
}

const th = {
  textAlign: "left", padding: "6px 8px", fontWeight: 600,
  borderBottom: "1px solid #e2e8f0", fontSize: 12,
};
const td = { padding: "4px 6px", borderBottom: "1px solid #f1f5f9" };
